import classNames from "classnames";
import { motion } from "framer-motion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { ArrowDown } from "components/icons/ArrowDown";
import { Cross } from "components/icons/Cross";
import { Info } from "components/icons/Info";
import { SmallSparkle } from "components/icons/SmallSparkle";
import { addNewClass, addNewGroup } from "formValidations/classification";
import { ConditionalLink } from "components/conditionalLink";
import { ModalControl, useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useWindowSize } from "hooks/useWindowSize";
import { Fragment, useState } from "react";
import ConditionalToolTip from "components/design/conditionalToolTip";
import {
  useAddTxnClassificationMutation,
  useConfirmTxnClassificationMutation,
  useCreateClassificationMutation,
  useDeleteTxnClassificationMutation,
  useGetAllClassificationQuery,
  useGetTxnClassificationsQuery,
} from "store/apis/classifications";
import {
  Class,
  ClassificationGroup,
  SuggestedClassification,
} from "types/Models/classification";
import { BackendError } from "types/utils/error";
import { BOOKS_SETTINGS_TABS } from "constants/booksSettings";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";

type ClassificationsProps = {
  transactionId: string;
  entityId: string;
};

type AddNewGroupProps = ModalControl & {
  onSuccess: (newGroup: ClassificationGroup) => void;
  entityId: string;
  showSettingsInfo?: boolean;
};

type AddNewClassProps = ModalControl & {
  onSuccess: (newClass: Class) => void;
  addClassToGroup: string | null;
  entityId: string;
  showSettingsInfo?: boolean;
};

const ClassificationSettingsInfo = () => {
  const { link } = useConstructInternalLink();
  return (
    <div className="t-flex t-items-center t-gap-1.5 t-text-body-sm-italic t-italic t-text-text-30">
      <span className="t-text-text-30 t-flex">
        <Info size="14" stroke="1.4" />
      </span>

      <span>
        You can manage all the groups and classifications created from Books
        Settings &gt;{" "}
        <ConditionalLink
          to={link(`/books/books-settings`, {
            moreQuery: { tab: BOOKS_SETTINGS_TABS.CLASSIFICATIONS },
          })}
          className="t-text-purple t-bg-surface-transparent hover:t-text-purple-80 hover:!t-underline"
        >
          Classifications
        </ConditionalLink>
        .
      </span>
    </div>
  );
};

export const AddNewGroup = ({
  entityId,
  isOpen,
  close,
  onSuccess,
  showSettingsInfo = true,
}: AddNewGroupProps) => {
  const { alertToast, successToast } = useToast();
  const { isMobile } = useWindowSize();
  const [createClassification] = useCreateClassificationMutation();

  const { data: classification = [] } = useGetAllClassificationQuery(
    { entityId },
    {
      skip: !entityId,
    }
  );

  const onSubmit = async (values: { name: string; class_names: string[] }) => {
    try {
      const result = await createClassification({
        entityId,
        payload: {
          group_name: values.name,
          class_names: values.class_names,
        },
      }).unwrap();

      onSuccess(result);
      successToast({
        message: `"${values.name}" with classes "${values.class_names.join(
          ", "
        )}" added successfully`,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large" asChild>
        <FormikForm
          onSubmit={onSubmit}
          initialValues={{ name: "", class_names: [] }}
          validateOnChange
          validationSchema={() => addNewGroup(classification)}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <Modal.Header>
                <Modal.Title>Add New Group</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-5">
                <TextInput
                  name="name"
                  label="Group name"
                  placeholder="Enter group name"
                  required
                />
                <Combobox
                  creatable
                  isMulti
                  withForm
                  name="class_names"
                  label="Add classes under this group"
                  placeholder="Enter class names"
                  required
                  openMenuOnClick={false}
                  menuPortalTarget={document.body}
                  menuPlacement={isMobile ? "top" : undefined}
                />
                {showSettingsInfo && <ClassificationSettingsInfo />}
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button disabled={isSubmitting}>Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  onClick={submitForm}
                  type="button"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};

export const AddNewClass = ({
  isOpen,
  close,
  onSuccess,
  addClassToGroup,
  entityId,
  showSettingsInfo = true,
}: AddNewClassProps) => {
  const { alertToast, successToast } = useToast();
  const { isMobile } = useWindowSize();
  const [createClassification] = useCreateClassificationMutation();

  const { data: classification = [] } = useGetAllClassificationQuery(
    { entityId },
    {
      skip: !entityId,
    }
  );

  const onSubmit = async (values: { name: string; class_names: string[] }) => {
    try {
      const result = await createClassification({
        entityId,
        payload: {
          group_name: values.name,
          class_names: values.class_names,
        },
      }).unwrap();

      onSuccess(result);
      successToast({
        message: `"${values.class_names.join(", ")}" added to group "${
          values.name
        }" successfully`,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="large" asChild>
        <FormikForm
          onSubmit={onSubmit}
          initialValues={{
            name: addClassToGroup || "",
            class_names: [],
          }}
          validateOnChange
          validationSchema={() => addNewClass(classification)}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <Modal.Header>
                <Modal.Title>Add New Class</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-5">
                <Combobox
                  isClearable={false}
                  withForm
                  name="name"
                  label="Group name"
                  placeholder="Select group name"
                  options={classification.map((group) => ({
                    label: group.name,
                    value: group.name,
                  }))}
                  defaultValue={{
                    label: addClassToGroup || "",
                    value: addClassToGroup || "",
                  }}
                  required
                  menuPortalTarget={document.body}
                />
                <Combobox
                  creatable
                  isMulti
                  withForm
                  name="class_names"
                  label="Add classes under this group"
                  placeholder="Enter class names"
                  required
                  openMenuOnClick={false}
                  menuPortalTarget={document.body}
                  menuPlacement={isMobile ? "top" : undefined}
                />
                {showSettingsInfo && <ClassificationSettingsInfo />}
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button disabled={isSubmitting}>Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  onClick={submitForm}
                  type="button"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                >
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};

type AssignedClassesProps = {
  assignedClass: ClassificationGroup;
  fullClassificationGroup: ClassificationGroup;
  entityId: string;
  assignedClassificationIds: string[];
  onRemove?: (classUuid: string) => void;
  onAssignClass?: (classUuid: string, groupUuid: string) => void;
  isLoading?: boolean;
  transactionId?: string;
  isReadOnly?: boolean;
};

export const AssignedClasses = ({
  assignedClass,
  fullClassificationGroup,
  entityId,
  assignedClassificationIds,
  onRemove: onRemoveProp,
  onAssignClass: onAssignClassProp,
  isLoading: isLoadingProp,
  transactionId,
  isReadOnly = false,
}: AssignedClassesProps) => {
  const addNewClassModal = useModal();
  const { alertToast, successToast } = useToast();
  const [removeClass, { isLoading: isRemoving }] =
    useDeleteTxnClassificationMutation();
  const [assignClass, { isLoading: isAdding }] =
    useAddTxnClassificationMutation();
  const { classes, name, uuid } = assignedClass;

  const className = classes?.[0]?.name || "";
  const classUuid = classes?.[0]?.uuid || "";

  const defaultRemove = async (classUuid: string) => {
    if (!transactionId) return;

    try {
      await removeClass({
        entityId,
        transactionId,
        classificationId: classUuid,
      }).unwrap();

      successToast({
        message: `"${className}" removed from the transaction`,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const defaultAssignClass = async (classUuid: string, groupUuid: string) => {
    if (!transactionId) return;

    try {
      await assignClass({
        entityId,
        transactionId,
        payload: {
          classification_id: classUuid,
        },
      }).unwrap();

      const className = fullClassificationGroup.classes.find(
        (c) => c.uuid === classUuid
      )?.name;

      successToast({
        message: `"${className}" added to the transaction`,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRemoveProp) {
      onRemoveProp(classUuid);
    } else {
      defaultRemove(classUuid);
    }
  };

  const handleAssignClass = (classUuid: string) => {
    if (onAssignClassProp) {
      onAssignClassProp(classUuid, uuid);
    } else {
      defaultAssignClass(classUuid, uuid);
    }
  };

  const isLoading = isLoadingProp ?? (isRemoving || isAdding);

  return (
    <>
      <Dropdown.Root>
        <motion.div
          layout
          key={uuid}
          animate={{ scale: [0.5, 1], opacity: [0, 1] }}
          transition={{ duration: 0.2 }}
          exit={{ opacity: 0 }}
          className={classNames({ "t-pointer-events-none": isReadOnly })}
        >
          <Tag tagType="dark_gray" icon={false}>
            <div className="t-flex t-items-center t-gap-1">
              <Dropdown.Trigger
                asChild
                disabled={!fullClassificationGroup || isLoading || isReadOnly}
              >
                <button className="all:unset">
                  <span className="t-text-body-sm t-text-text-30">{name}:</span>
                  <span className="t-text-subtext-sm t-text-text-60 t-ml-1">
                    {className}
                  </span>
                </button>
              </Dropdown.Trigger>
              <Button
                type="button"
                customType="ghost_icon"
                size="xsmall"
                className="t-flex t-items-center t-justify-center t-p-0 t-border-0 t-bg-transparent hover:t-opacity-70"
                onPointerDown={handleRemove}
                isLoading={isRemoving}
                disabled={isLoading || isReadOnly}
              >
                <Cross size="12" />
              </Button>
            </div>
          </Tag>
        </motion.div>

        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={4}
            align="start"
            side="bottom"
            className="t-max-h-60 t-overflow-auto"
          >
            {fullClassificationGroup.classes.map((classItem) => {
              const selected = assignedClassificationIds.includes(
                classItem.uuid
              );
              return (
                <Dropdown.Item
                  key={classItem.uuid}
                  onSelect={() =>
                    !selected && handleAssignClass(classItem.uuid)
                  }
                  className={classNames({
                    "t-bg-purple-0": selected,
                  })}
                >
                  <span>{classItem.name}</span>
                </Dropdown.Item>
              );
            })}
            <Dropdown.Item onSelect={addNewClassModal.open}>
              <Button customType="link" size="small" type="button">
                Add class
              </Button>
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <AddNewClass
        {...addNewClassModal}
        entityId={entityId}
        addClassToGroup={name}
        onSuccess={() => addNewClassModal.close()}
      />
    </>
  );
};

type AISuggestionListProps = {
  suggestions: SuggestedClassification[];
  onConfirm: (classUuid: string, groupUuid: string) => void;
  isConfirming?: boolean;
  // Render as Radix dropdown items (auto-close + built-in hover) vs plain buttons (side panel body).
  asDropdownItem?: boolean;
};

// Shared AI suggestion block: a purple sparkle header + one truncated, hoverable row per suggestion.
// Clicking a row confirms that group's suggestion. Reused by the table dropdown and the side panel.
export const AISuggestionList = ({
  suggestions,
  onConfirm,
  isConfirming = false,
  asDropdownItem = false,
}: AISuggestionListProps) => {
  if (suggestions.length === 0) return null;

  return (
    <div className="t-flex t-flex-col t-gap-0.5 t-py-1">
      <div className="t-flex t-items-center t-gap-1.5 t-px-2 t-py-1">
        <span className="t-flex-shrink-0 t-flex t-items-center t-text-purple">
          <SmallSparkle width={16} height={16} />
        </span>
        <span className="t-text-body-sm t-text-text-30">
          AI suggested classification
        </span>
      </div>
      {suggestions.map((suggestion) => {
        const label = (
          <ConditionalToolTip
            condition={
              <span className="t-text-body-sm">
                {suggestion.groupName}: {suggestion.className}
              </span>
            }
          >
            <span className="t-flex t-min-w-0 t-max-w-full t-items-center t-truncate">
              <span className="t-text-body-sm t-text-text-30">
                {suggestion.groupName}:
              </span>
              <span className="t-text-subtext-sm t-text-text-60 t-ml-1 t-truncate">
                {suggestion.className}
              </span>
            </span>
          </ConditionalToolTip>
        );

        if (asDropdownItem) {
          return (
            <Dropdown.Item
              key={suggestion.classUuid}
              disabled={isConfirming}
              onSelect={() =>
                onConfirm(suggestion.classUuid, suggestion.groupUuid)
              }
              // Match the group-row (SubTrigger) highlight shade, not the default item shade.
              className="t-pl-7 data-[highlighted]:!t-bg-neutral-0"
            >
              {label}
            </Dropdown.Item>
          );
        }

        return (
          <button
            key={suggestion.classUuid}
            type="button"
            disabled={isConfirming}
            onClick={() =>
              onConfirm(suggestion.classUuid, suggestion.groupUuid)
            }
            className="all:unset t-flex t-cursor-pointer t-items-center t-rounded t-px-3 t-py-2 t-pl-7 hover:t-bg-neutral-0"
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

type ClassificationSelectorContentProps = {
  assignedClassificationIds: string[];
  onAssignClass: (classUuid: string, groupUuid: string) => void;
  classification: ClassificationGroup[];
  suggestions?: SuggestedClassification[];
  onConfirmSuggestion?: (classUuid: string, groupUuid: string) => void;
  isAdding?: boolean;
  showAddGroup?: boolean;
  onAddGroup: () => void;
  onAddClass: (groupName: string) => void;
};

export const ClassificationSelectorContent = ({
  assignedClassificationIds,
  onAssignClass,
  classification,
  suggestions = [],
  onConfirmSuggestion,
  isAdding = false,
  showAddGroup = true,
  onAddGroup,
  onAddClass,
}: ClassificationSelectorContentProps) => {
  const { isMobile } = useWindowSize();
  const [openMobileGroupId, setOpenMobileGroupId] = useState<string | null>(
    null
  );

  const toggleMobileGroup = (groupId: string) => (e: Event) => {
    e.preventDefault();
    setOpenMobileGroupId((openId) => (openId === groupId ? null : groupId));
  };

  const renderGroupItems = (
    classes: ClassificationSelectorContentProps["classification"][number]["classes"] = [],
    groupId: string,
    groupName: string
  ) => (
    <>
      {classes.map((classItem) => {
        const selected = assignedClassificationIds.includes(classItem.uuid);
        return (
          <Dropdown.Item
            key={classItem.uuid}
            onSelect={() => !selected && onAssignClass(classItem.uuid, groupId)}
            disabled={isAdding}
            className={classNames({
              "t-bg-purple-0": selected,
            })}
          >
            <span>{classItem.name}</span>
          </Dropdown.Item>
        );
      })}
      <Dropdown.Item onSelect={() => onAddClass?.(groupName)}>
        <Button customType="link" size="small" type="button">
          Add class
        </Button>
      </Dropdown.Item>
    </>
  );

  return (
    <>
      {suggestions.length > 0 && onConfirmSuggestion && (
        <>
          <AISuggestionList
            suggestions={suggestions}
            onConfirm={onConfirmSuggestion}
            isConfirming={isAdding}
            asDropdownItem
          />
          <Dropdown.Separator />
        </>
      )}
      {classification.map(({ classes = [], name, uuid }) => {
        const mobileGroupState = openMobileGroupId === uuid ? "open" : "closed";

        return (
          <Fragment key={uuid}>
            {!isMobile && (
              <Dropdown.Sub>
                <Dropdown.SubTrigger>{name}</Dropdown.SubTrigger>
                <Dropdown.Portal>
                  <Dropdown.SubContent
                    sideOffset={8}
                    alignOffset={-5}
                    className="t-max-h-60 t-overflow-auto"
                  >
                    {renderGroupItems(classes, uuid, name)}
                  </Dropdown.SubContent>
                </Dropdown.Portal>
              </Dropdown.Sub>
            )}

            {/* On phones a group expands inline below itself instead of opening a side submenu. */}
            {isMobile && (
              <>
                <Dropdown.Item
                  onSelect={toggleMobileGroup(uuid)}
                  data-state={mobileGroupState}
                  className="t-group t-flex t-items-center t-justify-between"
                >
                  <span>{name}</span>
                  <span className="t-flex t-transition-transform group-data-[state=open]:t-rotate-180">
                    <ArrowDown />
                  </span>
                </Dropdown.Item>
                <div
                  data-state={mobileGroupState}
                  className="t-hidden t-pl-3 data-[state=open]:t-block"
                >
                  {renderGroupItems(classes, uuid, name)}
                </div>
              </>
            )}
          </Fragment>
        );
      })}
      {showAddGroup && (
        <Dropdown.Item onSelect={onAddGroup}>
          <Button customType="link" size="small" type="button">
            Add group
          </Button>
        </Dropdown.Item>
      )}
    </>
  );
};

type ClassificationSelectorProps = {
  entityId: string;
  assignedClassificationIds: string[];
  onAssignClass?: (classUuid: string, groupUuid: string) => void;
  isDisabled?: boolean;
  onUpgradeClick?: () => void;
  transactionId?: string;
  assignedClasses?: ClassificationGroup[];
  suggestions?: SuggestedClassification[];
  onConfirmSuggestion?: (classUuid: string, groupUuid: string) => void;
};

export const ClassificationSelector = ({
  entityId,
  assignedClassificationIds,
  onAssignClass: onAssignClassProp,
  isDisabled = false,
  onUpgradeClick,
  transactionId,
  assignedClasses,
  suggestions = [],
  onConfirmSuggestion,
}: ClassificationSelectorProps) => {
  const { alertToast, successToast } = useToast();
  const [assignClass, { isLoading: isAdding }] =
    useAddTxnClassificationMutation();
  const addNewGroupModal = useModal();
  const addNewClassModal = useModal();
  const [addClassToGroup, setAddClassToGroup] = useState<string | null>(null);

  const { data: classification = [] } = useGetAllClassificationQuery(
    { entityId },
    {
      skip: !entityId,
    }
  );

  const defaultOnAssignClass = async (classUuid: string, groupUuid: string) => {
    if (!transactionId) return;

    try {
      await assignClass({
        entityId,
        transactionId,
        payload: {
          classification_id: classUuid,
        },
      }).unwrap();

      const className = classification
        .find((g) => g.uuid === groupUuid)
        ?.classes.find((c) => c.uuid === classUuid)?.name;

      successToast({
        message: `"${className}" added to the transaction`,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  const handleAssignClass = (classUuid: string, groupUuid: string) => {
    if (onAssignClassProp) {
      onAssignClassProp(classUuid, groupUuid);
    } else {
      defaultOnAssignClass(classUuid, groupUuid);
    }
  };

  const handleAddClass = (groupName: string) => {
    setAddClassToGroup(groupName);
    addNewClassModal.open();
  };

  const handleAddGroup = () => {
    addNewGroupModal.open();
  };

  return (
    <>
      <Dropdown.Root>
        <Dropdown.Trigger asChild disabled={isDisabled}>
          <Button
            isLoading={isAdding}
            customType="link"
            size="small"
            type="button"
            disabled={isDisabled}
            onClick={isDisabled ? onUpgradeClick : undefined}
          >
            + Add
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={4}
            align="end"
            side="bottom"
            className="t-max-h-60 t-overflow-auto"
          >
            <ClassificationSelectorContent
              assignedClassificationIds={assignedClassificationIds}
              onAssignClass={handleAssignClass}
              classification={classification}
              suggestions={suggestions}
              onConfirmSuggestion={onConfirmSuggestion}
              isAdding={isAdding}
              showAddGroup={true}
              onAddGroup={handleAddGroup}
              onAddClass={handleAddClass}
            />
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <AddNewGroup
        {...addNewGroupModal}
        entityId={entityId}
        onSuccess={(newGroup: ClassificationGroup) => {
          addNewGroupModal.close();
        }}
      />
      <AddNewClass
        {...addNewClassModal}
        entityId={entityId}
        addClassToGroup={addClassToGroup}
        onSuccess={(newClass: Class) => {
          addNewClassModal.close();
          setAddClassToGroup(null);
        }}
      />
    </>
  );
};

export const Classifications = ({
  transactionId,
  entityId,
}: ClassificationsProps) => {
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );

  const { alertToast, successToast } = useToast();
  const [confirmTxnClassification] = useConfirmTxnClassificationMutation();

  const { data: assignedClasses = [], ...assignedClassesQueryState } =
    useGetTxnClassificationsQuery(
      {
        entityId,
        transactionId,
      },
      {
        skip: !entityId || !transactionId,
      }
    );

  const { data: allClassifications = [] } = useGetAllClassificationQuery(
    { entityId },
    {
      skip: !entityId,
    }
  );

  const assignedClassificationIds = assignedClasses.flatMap((group) =>
    group.classes.map((cls) => cls.uuid)
  );

  const suggestions: SuggestedClassification[] = assignedClasses.flatMap(
    (group) =>
      (group.suggestions ?? []).map((suggestion) => ({
        classUuid: suggestion.uuid,
        className: suggestion.name,
        groupUuid: group.uuid,
        groupName: group.name,
      }))
  );

  const handleConfirmSuggestion = async (classUuid: string) => {
    try {
      await confirmTxnClassification({
        entityId,
        transactionId,
        classificationId: classUuid,
      }).unwrap();
      successToast({ message: "AI classification applied to the transaction" });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  return (
    <Card.Root variant="secondary" borderRounded="lg">
      <Card.Header sticky={false}>
        <Card.Title>Classifications</Card.Title>
        <ClassificationSelector
          entityId={entityId}
          transactionId={transactionId}
          assignedClassificationIds={assignedClassificationIds}
          isDisabled={!hasWritePermission}
          suggestions={suggestions}
          onConfirmSuggestion={handleConfirmSuggestion}
        />
      </Card.Header>
      <Async.Root
        isLoading={assignedClassesQueryState.isLoading}
        isSuccess={assignedClassesQueryState.isSuccess}
        isEmpty={assignedClasses.length === 0}
        renderOnLoad={
          <Card.Body className="t-flex t-flex-wrap t-gap-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="t-h-6 t-w-20 t-inline-block" />
            ))}
          </Card.Body>
        }
      >
        <Async.Empty>
          <></>
        </Async.Empty>
        <Async.Success>
          <Card.Body className="t-flex t-flex-wrap t-gap-2">
            {assignedClasses.map((assignedClass) => {
              // Suggestion-only groups have no confirmed class — no chip; suggestion shows in "+ Add".
              if (assignedClass.classes.length === 0) return null;
              const fullGroup = allClassifications.find(
                (g) => g.uuid === assignedClass.uuid
              );
              if (!fullGroup) return null;

              return (
                <AssignedClasses
                  key={assignedClass.uuid}
                  assignedClass={assignedClass}
                  fullClassificationGroup={fullGroup}
                  entityId={entityId}
                  transactionId={transactionId}
                  assignedClassificationIds={assignedClassificationIds}
                  isReadOnly={!hasWritePermission}
                />
              );
            })}
          </Card.Body>
        </Async.Success>
      </Async.Root>
    </Card.Root>
  );
};
